/**
 * InlineMedicoForm
 *
 * Mini-formulario de creación rápida de médico remitente.
 * Se renderiza dentro del Popover del combobox de médico en OrdenFormDialog.
 *
 * Feature: inline-entity-creation-orden
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useCreateMedicoRemitente } from "@/hooks/use-medicos-remitentes";
import { ESPECIALIDADES_MEDICAS } from "@/hooks/use-medicos-remitentes";
import type { MedicoRemitenteListItem } from "@/hooks/use-medicos-remitentes";
import { useApiError } from "@/hooks/use-api-error";

// =============================================================================
// SCHEMA
// =============================================================================

function buildMedicoSchema(t: (key: string) => string) {
  return z.object({
    nombre_completo: z.string().min(2, t("ordenes.inline.validation.nameMin")),
    especialidad: z.string().optional(),
    celular: z.string().optional(),
  });
}

type InlineMedicoFormValues = {
  nombre_completo: string;
  especialidad?: string;
  celular?: string;
};

// =============================================================================
// PROPS
// =============================================================================

export interface InlineMedicoFormProps {
  /** Texto pre-rellenado desde el searchQuery del combobox */
  initialName: string;
  /** Callback al crear exitosamente — el padre maneja toast y cierre */
  onSuccess: (medico: MedicoRemitenteListItem) => void;
  /** Callback al cancelar — sin efectos secundarios */
  onCancel: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function InlineMedicoForm({ initialName, onSuccess, onCancel }: InlineMedicoFormProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  const schema = useMemo(() => buildMedicoSchema(t), [t]);

  const form = useForm<InlineMedicoFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre_completo: initialName,
      especialidad: "",
      celular: "",
    },
  });

  const createMedico = useCreateMedicoRemitente();

  const onSubmit = (values: InlineMedicoFormValues) => {
    // Parsear nombre_completo en primer_nombre + primer_apellido
    const parts = values.nombre_completo.trim().split(/\s+/);
    const primer_nombre = parts[0] ?? "";
    const primer_apellido = parts.slice(1).join(" ") || primer_nombre;

    createMedico.mutate(
      {
        primer_nombre,
        primer_apellido,
        especialidad: values.especialidad || undefined,
        celular: values.celular || undefined,
      },
      {
        onSuccess: (medicoCreado) => {
          // Construir MedicoRemitenteListItem desde la respuesta completa
          const listItem: MedicoRemitenteListItem = {
            id: medicoCreado.id,
            nombre_completo: medicoCreado.nombre_completo,
            especialidad: medicoCreado.especialidad,
            celular: medicoCreado.celular,
            es_tratante_gen: medicoCreado.es_tratante_gen,
            is_active: medicoCreado.is_active,
          };
          onSuccess(listItem);
        },
        onError: (err) => {
          toast.error(getErrorMessage(err));
        },
      }
    );
  };

  const isPending = createMedico.isPending;

  return (
    <div className="p-3 space-y-3 min-w-[280px]">
      <p className="text-sm font-medium">{t("ordenes.inline.newDoctor")}</p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          {/* Nombre completo */}
          <FormField
            control={form.control}
            name="nombre_completo"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">{t("ordenes.inline.fields.fullName")} *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isPending}
                    placeholder={t("ordenes.inline.fields.fullName")}
                    className="h-8 text-sm"
                    data-testid="inline-medico-nombre"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          {/* Especialidad */}
          <FormField
            control={form.control}
            name="especialidad"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">{t("ordenes.inline.fields.specialty")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={isPending}>
                  <FormControl>
                    <SelectTrigger className="h-8 text-sm" data-testid="inline-medico-especialidad">
                      <SelectValue placeholder={t("ordenes.inline.fields.specialty")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ESPECIALIDADES_MEDICAS.map((esp) => (
                      <SelectItem key={esp.value} value={esp.value}>
                        {esp.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          {/* Celular */}
          <FormField
            control={form.control}
            name="celular"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">{t("ordenes.inline.fields.phone")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isPending}
                    placeholder={t("ordenes.inline.fields.phone")}
                    className="h-8 text-sm"
                    data-testid="inline-medico-celular"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          {/* Acciones */}
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={onCancel}
              disabled={isPending}
              data-testid="inline-medico-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              className="flex-1"
              disabled={isPending}
              data-testid="inline-medico-submit"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  {t("common.saving")}
                </>
              ) : (
                t("common.save")
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
